import React, { useState, useEffect } from 'react';
import './Inventario.css';

/**
 * Módulo de Gestión de Usuarios
 * - Lista usuarios con rol, estado activo/inactivo
 * - Permite crear, editar y eliminar usuarios
 * - Define roles: admin, vendedor, cajero, almacen
 */
export default function Usuarios({ usuario }) {
  const [usuarios, setUsuarios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [filtroRol, setFiltroRol] = useState('todos');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  
  // Modal edición/creación
  const [modalAbierto, setModalAbierto] = useState(false);
  const [editando, setEditando] = useState(null); // id del usuario editando o null para nuevo
  const [form, setForm] = useState({
    nombre: '',
    email: '',
    password: '',
    rol: 'vendedor',
    activo: true
  });
  const [guardando, setGuardando] = useState(false);

  const roles = ['admin', 'vendedor', 'cajero', 'almacen'];

  useEffect(() => {
    console.log('Usuarios component mounted');
    cargarUsuarios();
  }, []);

  const cargarUsuarios = async () => {
    console.log('Iniciando carga de usuarios...');
    setCargando(true);
    setError(null);
    try {
      console.log('Fetching from: /api/usuarios');
      const res = await fetch('/api/usuarios');
      console.log('Response status:', res.status, 'OK:', res.ok);
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Error ${res.status}: ${errorText || 'Error al cargar usuarios'}`);
      }
      const data = await res.json();
      console.log('Usuarios cargados:', data); // Debug
      setUsuarios(data);
    } catch (e) {
      console.error('Error cargando usuarios:', e);
      setError(e.message);
    } finally {
      setCargando(false);
    }
  };

  const abrirModalNuevo = () => {
    setEditando(null);
    setForm({ nombre: '', email: '', password: '', rol: 'vendedor', activo: true });
    setModalAbierto(true);
  };

  const abrirModalEditar = (u) => {
    setEditando(u.id_usuario);
    setForm({
      nombre: u.nombre,
      email: u.email,
      password: '', // no mostramos la contraseña actual
      rol: u.rol || 'vendedor',
      activo: u.activo !== undefined ? u.activo : true
    });
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setEditando(null);
    setForm({ nombre: '', email: '', password: '', rol: 'vendedor', activo: true });
  };

  const guardarUsuario = async () => {
    if (!form.nombre.trim() || !form.email.trim()) {
      alert('Nombre y email son obligatorios');
      return;
    }
    if (!editando && !form.password.trim()) {
      alert('La contraseña es obligatoria para usuarios nuevos');
      return;
    }

    setGuardando(true);
    try {
      const payload = {
        nombre: form.nombre,
        email: form.email,
        rol: form.rol,
        activo: form.activo
      };
      if (form.password.trim()) {
        payload.password = form.password;
      }

      if (editando) {
        // Actualizar
        const res = await fetch(`/api/usuarios/${editando}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error('Error al actualizar usuario');
      } else {
        // Crear
        const res = await fetch('/api/usuarios', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error('Error al crear usuario');
      }

      await cargarUsuarios();
      cerrarModal();
    } catch (e) {
      alert(e.message);
    } finally {
      setGuardando(false);
    }
  };

  const eliminarUsuario = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este usuario?')) return;
    try {
      const res = await fetch(`/api/usuarios/${id}`, {
        method: 'DELETE'
      });
      if (!res.ok) throw new Error('Error al eliminar usuario');
      await cargarUsuarios();
    } catch (e) {
      alert(e.message);
    }
  };

  const usuariosFiltrados = usuarios.filter(u => {
    const matchBusqueda = !busqueda || 
      (u.nombre || '').toLowerCase().includes(busqueda.toLowerCase()) ||
      (u.email || '').toLowerCase().includes(busqueda.toLowerCase());
    const matchRol = filtroRol === 'todos' || u.rol === filtroRol;
    const matchEstado = filtroEstado === 'todos' || 
      (filtroEstado === 'activo' && u.activo) ||
      (filtroEstado === 'inactivo' && !u.activo);
    return matchBusqueda && matchRol && matchEstado;
  });

  const contadores = {
    total: usuarios.length,
    activos: usuarios.filter(u => u.activo).length,
    admin: usuarios.filter(u => u.rol === 'admin').length,
    vendedor: usuarios.filter(u => u.rol === 'vendedor').length
  };

  if (cargando) return <div style={{padding:20}}>Cargando usuarios...</div>;
  if (error) return (
    <div style={{padding:20}}>
      <div style={{color:'#b71c1c',marginBottom:10}}>Error: {error}</div>
      <button onClick={cargarUsuarios} className="btn-modern primary">Reintentar</button>
      <div style={{marginTop:12,fontSize:'12px',color:'#666'}}>
        <p>Verifica que:</p>
        <ul>
          <li>El servidor backend esté corriendo en el mismo servidor que el frontend</li>
          <li>La tabla usuarios exista en la base de datos</li>
          <li>No haya problemas de CORS</li>
        </ul>
      </div>
    </div>
  );

  return (
    <div style={{padding:'18px 24px'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
        <h2 style={{margin:0}}>Usuarios</h2>
        <button onClick={abrirModalNuevo} className="btn-modern primary">+ Agregar Usuario</button>
      </div>

      {/* Contadores */}
      <div style={{display:'flex',gap:12,marginBottom:16,flexWrap:'wrap'}}>
        <div className="chip">Total: {contadores.total}</div>
        <div className="chip">Activos: {contadores.activos}</div>
        <div className="chip">Admins: {contadores.admin}</div>
        <div className="chip">Vendedores: {contadores.vendedor}</div>
      </div>

      {/* Filtros */}
      <div style={{display:'flex',gap:12,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
        <input
          type="text"
          placeholder="Buscar por nombre o email..."
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          style={{flex:'1 1 200px',maxWidth:300}}
        />
        <select value={filtroRol} onChange={e => setFiltroRol(e.target.value)} style={{minWidth:120}}>
          <option value="todos">Todos los roles</option>
          {roles.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} style={{minWidth:120}}>
          <option value="todos">Todos los estados</option>
          <option value="activo">Activos</option>
          <option value="inactivo">Inactivos</option>
        </select>
        <button onClick={cargarUsuarios} className="btn-modern outline">↻</button>
      </div>

      {/* Tabla */}
      <div style={{overflowX:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px'}}>
          <thead>
            <tr style={{background:'#f5f5f5'}}>
              <th style={{padding:'8px',border:'1px solid #ddd',textAlign:'left'}}>ID</th>
              <th style={{padding:'8px',border:'1px solid #ddd',textAlign:'left'}}>Nombre</th>
              <th style={{padding:'8px',border:'1px solid #ddd',textAlign:'left'}}>Email</th>
              <th style={{padding:'8px',border:'1px solid #ddd',textAlign:'left'}}>Rol</th>
              <th style={{padding:'8px',border:'1px solid #ddd',textAlign:'center'}}>Estado</th>
              <th style={{padding:'8px',border:'1px solid #ddd',textAlign:'center'}}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {usuariosFiltrados.map(u => (
              <tr key={u.id_usuario}>
                <td style={{padding:'8px',border:'1px solid #ddd'}}>{u.id_usuario}</td>
                <td style={{padding:'8px',border:'1px solid #ddd'}}>{u.nombre}</td>
                <td style={{padding:'8px',border:'1px solid #ddd'}}>{u.email}</td>
                <td style={{padding:'8px',border:'1px solid #ddd'}}>
                  <span className="chip" style={{
                    background: u.rol === 'admin' ? '#1565c0' : u.rol === 'vendedor' ? '#2e7d32' : '#f57c00',
                    color:'#fff',
                    fontSize:'11px'
                  }}>
                    {u.rol || 'vendedor'}
                  </span>
                </td>
                <td style={{padding:'8px',border:'1px solid #ddd',textAlign:'center'}}>
                  <span className="chip" style={{
                    background: u.activo ? '#4caf50' : '#9e9e9e',
                    color:'#fff',
                    fontSize:'11px'
                  }}>
                    {u.activo ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
                <td style={{padding:'8px',border:'1px solid #ddd',textAlign:'center'}}>
                  <div style={{display:'flex',gap:6,justifyContent:'center',flexWrap:'wrap'}}>
                    <button onClick={() => abrirModalEditar(u)} className="btn-modern outline" style={{fontSize:'11px',padding:'4px 8px'}}>
                      Editar
                    </button>
                    <button onClick={() => eliminarUsuario(u.id_usuario)} className="btn-cancelar" style={{fontSize:'11px',padding:'4px 8px'}}>
                      Eliminar
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {usuariosFiltrados.length === 0 && (
              <tr>
                <td colSpan={6} style={{padding:20,textAlign:'center',opacity:.6}}>
                  No se encontraron usuarios
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {modalAbierto && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:10000}}>
          <div style={{background:'#fff',padding:'20px 24px',borderRadius:8,width:460,maxWidth:'92%',boxShadow:'0 4px 14px rgba(0,0,0,0.3)'}}>
            <h3 style={{marginTop:0}}>{editando ? 'Editar Usuario' : 'Nuevo Usuario'}</h3>
            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              <div>
                <label style={{display:'block',fontSize:'12px',fontWeight:600,marginBottom:4}}>Nombre</label>
                <input
                  type="text"
                  value={form.nombre}
                  onChange={e => setForm({...form, nombre: e.target.value})}
                  style={{width:'100%'}}
                />
              </div>
              <div>
                <label style={{display:'block',fontSize:'12px',fontWeight:600,marginBottom:4}}>Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm({...form, email: e.target.value})}
                  style={{width:'100%'}}
                />
              </div>
              <div>
                <label style={{display:'block',fontSize:'12px',fontWeight:600,marginBottom:4}}>
                  Contraseña {editando && <span style={{fontSize:'11px',opacity:.7}}>(dejar vacío para no cambiar)</span>}
                </label>
                <input
                  type="password"
                  value={form.password}
                  onChange={e => setForm({...form, password: e.target.value})}
                  style={{width:'100%'}}
                  placeholder={editando ? 'Dejar vacío si no cambias' : 'Contraseña'}
                />
              </div>
              <div>
                <label style={{display:'block',fontSize:'12px',fontWeight:600,marginBottom:4}}>Rol</label>
                <select
                  value={form.rol}
                  onChange={e => setForm({...form, rol: e.target.value})}
                  style={{width:'100%'}}
                >
                  {roles.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label style={{display:'flex',alignItems:'center',gap:6,fontSize:'13px',cursor:'pointer'}}>
                  <input
                    type="checkbox"
                    checked={form.activo}
                    onChange={e => setForm({...form, activo: e.target.checked})}
                  />
                  Usuario activo
                </label>
              </div>
            </div>
            <div style={{display:'flex',gap:10,marginTop:18,justifyContent:'flex-end'}}>
              <button onClick={cerrarModal} className="btn-cancelar" disabled={guardando}>
                Cancelar
              </button>
              <button onClick={guardarUsuario} className="btn-modern primary" disabled={guardando}>
                {guardando ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
